import {
  Notation,
  NotationSystem,
  NoteConversionOptions,
} from '@teetsh/app/src/types/notationSystem';
import { getNotationFromValue } from './getNotationFromValue';
import { getClosestValueFromNotationSystem } from './getClosestValueFromNotationSystem';
import colors from '@teetsh/app/src/colors';
import _round from 'lodash-es/round';
import { getContrastBasedOnValue } from 'utils/notationSystems/getContrastBasedOnValue';
import { Note } from 'containers/CarnetsDeNotes/types/carnetsDeNotes';
import { ABS, NE } from 'containers/CarnetsDeNotes/constants';

function generateEquidistantValues(
  minValue: number,
  maxValue: number,
  targetCount: number = 10,
): number[] {
  const range = maxValue - minValue;

  if (range <= targetCount) {
    const values: number[] = [];
    for (let i = minValue; i <= maxValue; i++) {
      values.push(i);
    }
    return values;
  }

  const step = range / targetCount;
  const values: number[] = [];
  for (let i = 0; i <= targetCount; i++) {
    values.push(_round(minValue + i * step, 2));
  }

  return values;
}

export const getNotationsHelperAttributes = (
  notationSystem: Partial<NotationSystem> & {
    id: string;
    name: string;
    values: Notation[];
  },
) => {
  const isNumerical = notationSystem?.type === 'numerical';

  if (isNumerical) {
    const maxValue = notationSystem?.maxValue ?? 100;
    const minValue = notationSystem?.minValue ?? 0;
    const noMaxValue = notationSystem?.noMaxValue || false;

    const getValue = (
      value: number,
      options?: NoteConversionOptions,
    ): number => {
      if (value < 0) {
        return value;
      }

      if (noMaxValue) {
        // For noMaxValue systems, values are raw numbers
        if (options?.noteMax !== undefined) {
          const originalMax = options.noteMax;
          const originalMin = options.noteMin ?? 0;
          const currentMax = maxValue;
          const currentMin = minValue;

          // If value exceeds stored scale, it's bonus points - always preserve for calculations
          if (value > originalMax) {
            return value;
          }

          // If value is within stored scale, convert to current scale
          const normalizedValue =
            (value - originalMin) / (originalMax - originalMin);
          return _round(
            normalizedValue * (currentMax - currentMin) + currentMin,
            2,
          );
        }
        return value;
      }

      // For standard and custom scale systems:
      // When no options provided: value is a percentage (0-100), convert to scale
      // When options provided: value is on original scale, convert to current scale
      if (
        !options ||
        (options.noteMax === undefined && options.noteMin === undefined)
      ) {
        // No options: treat value as percentage, convert to current scale
        return _round(_round(maxValue * Math.min(value, 100), 2) / 100, 2);
      }

      // Options provided: convert from original scale to current scale
      const originalMax = options.noteMax ?? maxValue;
      const originalMin = options.noteMin ?? minValue;
      const currentMax = maxValue;
      const currentMin = minValue;

      // Cap at 100% for regular systems (values can't exceed the scale max)
      const cappedValue = Math.min(value, originalMax);

      // Convert: (value - originalMin) / (originalMax - originalMin) * (currentMax - currentMin) + currentMin
      const normalizedValue =
        (cappedValue - originalMin) / (originalMax - originalMin);
      return _round(
        normalizedValue * (currentMax - currentMin) + currentMin,
        2,
      );
    };

    return {
      id: notationSystem.id,
      name: notationSystem.name,
      type: 'numerical',
      minValue,
      maxValue,
      noMaxValue,
      values: notationSystem.values,

      getNotationFromValue: (value: number) => {
        if (value === ABS.value) {
          return ABS.label;
        }
        if (value === NE.value) {
          return NE.label;
        }

        // Format the value (no conversion - getValue should be called before this)
        return value.toLocaleString();
      },

      convertNote: (n: Note) => {
        const convertedNoteValue =
          n.note !== undefined
            ? getValue(n.note, {
                noteMax: n.notationSystemMax ?? 100,
                noteMin: n.notationSystemMin ?? 0,
              })
            : undefined;
        return {
          ...n,
          note: convertedNoteValue,
          notationSystemMax: notationSystem.maxValue,
          notationSystemMin: notationSystem.minValue,
        };
      },

      getNoteColorContrast: (
        note?: number,
        options?: NoteConversionOptions,
      ) => {
        const effectiveMax = options?.noteMax ?? maxValue;

        if (note === undefined) {
          return undefined;
        }
        if (note < 0) {
          return colors.muted;
        }

        // Normalize to percentage based on the effective max
        const percentage = Math.min(100, (note / effectiveMax) * 100);
        return getContrastBasedOnValue(percentage);
      },

      getClosestValueFromNotationSystem: (value: number) => value,

      graphValuesYAxis: (max?: number) => {
        const effectiveMax = max ?? maxValue;
        return generateEquidistantValues(minValue, effectiveMax);
      },
      getValue,
    } as NotationSystem;
  }

  // Handle competency notation systems (default)
  // Define getValue function first so getNoteColorContrast can use it
  const getValue = (value: number, options?: NoteConversionOptions) => {
    if (value < 0) {
      return value;
    }

    // When options provided, convert from original scale to 0-100 percentage scale
    if (options?.noteMax !== undefined) {
      const originalMax = options.noteMax;
      const originalMin = options.noteMin ?? 0;

      // Convert from original scale to 0-100 percentage scale
      const normalizedValue =
        (value - originalMin) / (originalMax - originalMin);
      const percentage = normalizedValue * 100;

      // Cap at 100% for competency systems (they represent percentages 0-100%)
      return _round(Math.min(percentage, 100), 2);
    }

    // No options: value is already on 0-100 scale
    return value;
  };

  return {
    id: notationSystem.id,
    name: notationSystem.name,
    type: notationSystem?.type || 'competency',
    minValue: 0,
    maxValue: 100,
    noMaxValue: false,
    values: notationSystem.values,

    getNotationFromValue: (value: number) =>
      getNotationFromValue(value, notationSystem.values),

    convertNote: (n: Note) => {
      const convertedNoteValue =
        n.note !== undefined
          ? getValue(n.note, {
              noteMax: n.notationSystemMax ?? 100,
              noteMin: n.notationSystemMin ?? 0,
            })
          : undefined;
      return {
        ...n,
        note: convertedNoteValue,
        notationSystemMax: notationSystem.maxValue,
        notationSystemMin: notationSystem.minValue,
      };
    },

    getNoteColorContrast: (note?: number, options?: NoteConversionOptions) => {
      if (note === undefined) {
        return undefined;
      }
      if (note < 0) {
        return colors.muted;
      }

      // Convert the note from its original scale to 0-100 percentage scale
      const convertedNote = getValue(note, options);

      // Find the closest notation value based on the converted note
      const value = getClosestValueFromNotationSystem(
        convertedNote,
        notationSystem.values,
      );

      return getContrastBasedOnValue(value);
    },

    getClosestValueFromNotationSystem: (value: number) =>
      getClosestValueFromNotationSystem(value, notationSystem.values),

    graphValuesYAxis: () =>
      notationSystem.values
        .map(notation => notation.value)
        .filter(value => value >= 0)
        .sort((a, b) => a - b),

    getValue,
  } as NotationSystem;
};
